import { ref, onValue, update, push } from 'firebase/database';
import { database } from '../firebase';
import { getAnalytics } from './analyticsService';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL;
const EMAIL_TRACKING_PIXEL_URL = process.env.REACT_APP_EMAIL_TRACKING_PIXEL_URL;

// Email tracking and analytics
const trackEmailEvent = async (emailId, eventType, metadata = {}) => {
  try {
    const trackingRef = ref(database, 'emailTracking');
    await push(trackingRef, {
      emailId,
      eventType,
      timestamp: new Date().toISOString(),
      metadata,
    });
  } catch (error) {
    console.error('Error tracking email event:', error);
  }
};

// Enhanced security with rate limiting
const rateLimiter = {
  attempts: {},
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes

  isRateLimited: (email) => {
    const now = Date.now();
    const attempt = rateLimiter.attempts[email] || { count: 0, timestamp: now };
    
    if (now - attempt.timestamp > rateLimiter.windowMs) {
      attempt.count = 0;
      attempt.timestamp = now;
    }
    
    attempt.count++;
    rateLimiter.attempts[email] = attempt;
    
    return attempt.count > rateLimiter.maxAttempts;
  },

  reset: (email) => {
    delete rateLimiter.attempts[email];
  },
};

// Email template with tracking pixel
const addTrackingPixel = (html, emailId) => {
  return `${html}
    <img src="${EMAIL_TRACKING_PIXEL_URL}?id=${emailId}" alt="" width="1" height="1" style="display:none" />
  `;
};

export const setupEmailNotifications = () => {
  const submissionsRef = ref(database, 'demoClasses');
  
  onValue(submissionsRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const submissions = Object.entries(data).map(([id, submission]) => ({
        id,
        ...submission,
      }));

      // Check for new submissions (status: pending)
      const newSubmissions = submissions.filter(
        (sub) => sub.status === 'pending' && !sub.notificationSent
      );

      // Send notifications for new submissions
      newSubmissions.forEach(async (submission) => {
        await sendNotification(submission);
      });

      // Check for upcoming classes (24 hours before)
      const upcomingClasses = submissions.filter(
        (sub) => sub.status === 'confirmed' && !sub.reminderSent
      );

      upcomingClasses.forEach(async (submission) => {
        const classDate = new Date(submission.preferredDate);
        const now = new Date();
        const hoursUntilClass = (classDate - now) / (1000 * 60 * 60);

        if (hoursUntilClass <= 24 && hoursUntilClass > 0) {
          await sendReminderEmail(submission);
        }
      });
    }
  });
};

const sendNotification = async (submission) => {
  try {
    // Send email to admin
    await sendAdminEmail(submission);
    
    // Send confirmation email to student
    await sendStudentEmail(submission);
    
    // Mark notification as sent
    const submissionRef = ref(database, `demoClasses/${submission.id}`);
    await update(submissionRef, {
      notificationSent: true,
      notificationSentAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error sending notification:', error);
  }
};

const sendAdminEmail = async (submission) => {
  const emailData = {
    to: ADMIN_EMAIL,
    subject: 'New Demo Class Request',
    html: `
      <h2>New Demo Class Request</h2>
      <p><strong>Student Details:</strong></p>
      <ul>
        <li>Name: ${submission.name}</li>
        <li>Email: ${submission.email}</li>
        <li>Phone: ${submission.phone}</li>
        <li>Olympiad: ${submission.olympiad}</li>
        <li>Class: ${submission.class}</li>
        <li>Preferred Date: ${new Date(submission.preferredDate).toLocaleDateString()}</li>
        <li>Preferred Time: ${submission.preferredTime}</li>
      </ul>
      ${submission.message ? `<p><strong>Additional Message:</strong> ${submission.message}</p>` : ''}
      <p><a href="${window.location.origin}/admin">View in Dashboard</a></p>
    `,
  };

  await sendEmail(emailData);
};

const sendStudentEmail = async (submission) => {
  const emailData = {
    to: submission.email,
    subject: 'Demo Class Request Received',
    html: `
      <h2>Thank you for your interest!</h2>
      <p>Dear ${submission.name},</p>
      <p>We have received your request for a demo class in ${submission.olympiad}.</p>
      <p><strong>Your Request Details:</strong></p>
      <ul>
        <li>Olympiad: ${submission.olympiad}</li>
        <li>Class: ${submission.class}</li>
        <li>Preferred Date: ${new Date(submission.preferredDate).toLocaleDateString()}</li>
        <li>Preferred Time: ${submission.preferredTime}</li>
      </ul>
      <p>Our team will review your request and contact you shortly to confirm the demo class schedule.</p>
      <p>If you have any questions, please don't hesitate to contact us.</p>
      <p>Best regards,<br>Junior Olympiad Team</p>
    `,
  };

  await sendEmail(emailData);
};

export const sendStatusUpdateEmail = async (submission, newStatus) => {
  try {
    const emailData = {
      to: submission.email,
      subject: `Demo Class Status Update - ${newStatus}`,
      html: getStatusUpdateTemplate(submission, newStatus),
    };

    await sendEmail(emailData);

    // Update submission with new status and notification sent
    const submissionRef = ref(database, `demoClasses/${submission.id}`);
    await update(submissionRef, {
      status: newStatus,
      statusUpdatedAt: new Date().toISOString(),
      statusUpdateNotificationSent: true,
    });
  } catch (error) {
    console.error('Error sending status update email:', error);
    throw error;
  }
};

export const sendReminderEmail = async (submission) => {
  try {
    const emailData = {
      to: submission.email,
      subject: 'Reminder: Upcoming Demo Class',
      html: `
        <h2>Demo Class Reminder</h2>
        <p>Dear ${submission.name},</p>
        <p>This is a reminder for your upcoming demo class:</p>
        <p><strong>Class Details:</strong></p>
        <ul>
          <li>Olympiad: ${submission.olympiad}</li>
          <li>Class: ${submission.class}</li>
          <li>Date: ${new Date(submission.preferredDate).toLocaleDateString()}</li>
          <li>Time: ${submission.preferredTime}</li>
        </ul>
        <p>Please ensure you have:</p>
        <ul>
          <li>Stable internet connection</li>
          <li>Required study materials</li>
          <li>Notebook and pen</li>
        </ul>
        <p>If you need to reschedule, please contact us at least 24 hours in advance.</p>
        <p>Best regards,<br>Junior Olympiad Team</p>
      `,
    };

    await sendEmail(emailData);

    // Mark reminder as sent
    const submissionRef = ref(database, `demoClasses/${submission.id}`);
    await update(submissionRef, {
      reminderSent: true,
      reminderSentAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error sending reminder email:', error);
    throw error;
  }
};

export const sendFeedbackRequestEmail = async (submission) => {
  try {
    const emailData = {
      to: submission.email,
      subject: 'Share Your Experience - Demo Class Feedback',
      html: `
        <h2>We Value Your Feedback</h2>
        <p>Dear ${submission.name},</p>
        <p>Thank you for attending the demo class in ${submission.olympiad}.</p>
        <p>We would love to hear about your experience and suggestions to improve our services.</p>
        <p>Please take a moment to share your feedback by clicking the link below:</p>
        <p><a href="${window.location.origin}/feedback/${submission.id}">Share Your Feedback</a></p>
        <p>Your feedback helps us provide better learning experiences for all students.</p>
        <p>Best regards,<br>Junior Olympiad Team</p>
      `,
    };

    await sendEmail(emailData);

    // Mark feedback request as sent
    const submissionRef = ref(database, `demoClasses/${submission.id}`);
    await update(submissionRef, {
      feedbackRequestSent: true,
      feedbackRequestSentAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error sending feedback request email:', error);
    throw error;
  }
};

const getStatusUpdateTemplate = (submission, newStatus) => {
  const templates = {
    confirmed: `
      <h2>Demo Class Confirmed!</h2>
      <p>Dear ${submission.name},</p>
      <p>Your demo class request has been confirmed.</p>
      <p><strong>Class Details:</strong></p>
      <ul>
        <li>Olympiad: ${submission.olympiad}</li>
        <li>Class: ${submission.class}</li>
        <li>Date: ${new Date(submission.preferredDate).toLocaleDateString()}</li>
        <li>Time: ${submission.preferredTime}</li>
      </ul>
      <p>Please prepare the following:</p>
      <ul>
        <li>Stable internet connection</li>
        <li>Required study materials</li>
        <li>Notebook and pen</li>
      </ul>
      <p>We look forward to seeing you in class!</p>
      <p>Best regards,<br>Junior Olympiad Team</p>
    `,
    cancelled: `
      <h2>Demo Class Cancelled</h2>
      <p>Dear ${submission.name},</p>
      <p>Your demo class request has been cancelled.</p>
      <p><strong>Class Details:</strong></p>
      <ul>
        <li>Olympiad: ${submission.olympiad}</li>
        <li>Class: ${submission.class}</li>
        <li>Date: ${new Date(submission.preferredDate).toLocaleDateString()}</li>
        <li>Time: ${submission.preferredTime}</li>
      </ul>
      <p>If you would like to reschedule, please visit our website and submit a new request.</p>
      <p>Best regards,<br>Junior Olympiad Team</p>
    `,
    completed: `
      <h2>Demo Class Completed</h2>
      <p>Dear ${submission.name},</p>
      <p>Thank you for attending the demo class in ${submission.olympiad}.</p>
      <p>We hope you found the session helpful and informative.</p>
      <p>If you have any questions or would like to enroll in our regular classes, please don't hesitate to contact us.</p>
      <p>Best regards,<br>Junior Olympiad Team</p>
    `,
  };

  return templates[newStatus] || '';
};

const sendEmail = async (emailData) => {
  try {
    const response = await fetch(EMAIL_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData),
    });

    if (!response.ok) {
      throw new Error('Failed to send email');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
}; 